import { FieldSelect, Form, FormikForm, FormRowField, Label } from "@skilitics/designer-ui-components";
import { Operator } from "@skilitics/judy";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { isTernaryOperator, operatorToEnglish } from "../../effects-conditions";
import { numberOperators } from "../../effects-conditions/condition-builders/shared";
import { FieldCssInput } from "../properties/FieldCssInput";
import type { ResponsiveConditionConfig } from "./responsiveCondition";

export type ResponsiveFormValues = Omit<ResponsiveConditionConfig, "id">;

type Props = {
  initialValues: ResponsiveFormValues;
  onSubmit: (values: ResponsiveFormValues) => void;
};

const operatorOptions = numberOperators
  .filter((value) => value !== Operator.EQ && value !== Operator.NE)
  .map((value) => ({ value, label: operatorToEnglish(value) ?? "" }));

const dimensionOptions = [
  {
    value: "width",
    label: "Width",
  },
  {
    value: "height",
    label: "Height",
  },
];

export const Responsive = ({ initialValues, onSubmit }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <FormikForm<ResponsiveFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      submitOnBlur
      formatStrings={formatStrings}
      dataTest="responsive-form"
    >
      {({ values }) => {
        const isTernary = isTernaryOperator(values.operator);

        const label = values.dimension === "width" ? "W" : "H";

        return (
          <Form $labelAlignment="inline">
            <FormRowField<ResponsiveFormValues> name="dimension" explicitLabel>
              <Label>{formatMessage("dimensionLabel")}</Label>
              <FieldSelect options={dimensionOptions} placeholder={formatMessage("dimensionLabel")} />
            </FormRowField>

            <FormRowField<ResponsiveFormValues> name="operator" explicitLabel>
              <Label>{formatMessage("operatorLabel")}</Label>
              <FieldSelect options={operatorOptions} placeholder={formatMessage("operatorPlaceholder")} />
            </FormRowField>

            {!isTernary && (
              <FormRowField<ResponsiveFormValues> name="minimum">
                <Label>{formatMessage("valueLabel")}</Label>
                <FieldCssInput label={label} propertyName={values.dimension} />
              </FormRowField>
            )}

            {isTernary && (
              <>
                <FormRowField<ResponsiveFormValues> name="minimum">
                  <Label>{formatMessage("from")}</Label>
                  <FieldCssInput label={label} propertyName={values.dimension} />
                </FormRowField>

                <FormRowField<ResponsiveFormValues> name="maximum">
                  <Label>{formatMessage("maximumLabel")}</Label>
                  <FieldCssInput label={label} propertyName={values.dimension} />
                </FormRowField>
              </>
            )}
          </Form>
        );
      }}
    </FormikForm>
  );
};

const formatStrings = {
  sectionHeading: {
    en: "Responsive {propertyName}",
  },
  sectionHelperText: {
    en: "Change the visibility of the container based on canvas responsiveness.",
  },
  dimensionLabel: {
    en: "Dimension",
  },
  operatorLabel: {
    en: "Operator",
  },
  minimumLabel: {
    en: "W",
  },
  from: {
    en: "From",
  },
  maximumLabel: {
    en: "To",
  },
  operatorPlaceholder: {
    en: "Select an operator",
  },
  valueLabel: {
    en: "Value",
  },
} satisfies FormatMessages;
